import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import invoiceService from '../../services/invoiceService';
import api from '../../services/api';
import supplierService from '../../services/supplierService';
import type { Invoice, InvoiceAttachment, InvoiceStatus, Supplier } from '../../types/venue';

const INVOICE_STATUSES: { value: InvoiceStatus; label: string }[] = [
  { value: 'draft', label: 'Draft' },
  { value: 'open',  label: 'Open' },
  { value: 'void',  label: 'Void' },
];

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  venueId: string;
  purchaseOrderId: string;
  /** Default supplier ID — used as initial value on create. */
  supplierId: string;
  /** Pass an existing invoice to edit. Omit to create. */
  invoice?: Invoice;
}

const InvoiceDialog: React.FC<Props> = ({
  open,
  onClose,
  onSuccess,
  venueId,
  purchaseOrderId,
  supplierId: defaultSupplierId,
  invoice,
}) => {
  const isEdit = !!invoice;

  const [suppliers, setSuppliers]         = useState<Supplier[]>([]);
  const [supplierId, setSupplierId]       = useState(defaultSupplierId);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate]     = useState('');
  const [dueDate, setDueDate]             = useState('');
  const [totalAmount, setTotalAmount]     = useState('');
  const [notes, setNotes]                 = useState('');
  const [status, setStatus]               = useState<InvoiceStatus>('draft');

  // Staged file for create mode — uploaded immediately after invoice is created
  const [pendingFile, setPendingFile]       = useState<File | null>(null);

  // Edit-mode attachment state
  const [attachments, setAttachments]       = useState<InvoiceAttachment[]>([]);
  const [uploading, setUploading]           = useState(false);
  const [uploadError, setUploadError]       = useState<string | null>(null);
  const [openingId, setOpeningId]           = useState<string | null>(null);
  const [openError, setOpenError]           = useState<string | null>(null);
  const [deletingId, setDeletingId]         = useState<string | null>(null);
  const [deleteError, setDeleteError]       = useState<string | null>(null);
  const fileInputRef                        = useRef<HTMLInputElement>(null);

  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState<string | null>(null);
  const [touched, setTouched] = useState(false);

  // Fetch supplier list once on first open
  useEffect(() => {
    if (open && suppliers.length === 0) {
      supplierService.getAllSuppliers(1, 500)
        .then(r => setSuppliers(r.data ?? []))
        .catch(() => { /* non-blocking */ });
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Populate / reset fields whenever the dialog opens or the target invoice changes
  useEffect(() => {
    if (open) {
      setSupplierId(invoice?.supplierId ?? defaultSupplierId);
      setInvoiceNumber(invoice?.invoiceNumber ?? '');
      setInvoiceDate(invoice?.invoiceDate ? invoice.invoiceDate.slice(0, 10) : '');
      setDueDate(invoice?.dueDate ? invoice.dueDate.slice(0, 10) : '');
      setTotalAmount(invoice?.totalAmount != null ? String(invoice.totalAmount) : '');
      setNotes(invoice?.notes ?? '');
      setStatus(invoice?.status ?? 'draft');
      setAttachments(invoice?.attachments ?? []);
      setPendingFile(null);
      setUploadError(null);
      setOpenError(null);
      setDeleteError(null);
      setError(null);
      setTouched(false);
    }
  }, [open, invoice, defaultSupplierId]);

  const supplierIdError    = touched && !supplierId ? 'Supplier is required' : null;
  const invoiceNumberError = touched && invoiceNumber.trim().length === 0
    ? 'Invoice number is required'
    : null;
  const totalAmountError   = touched && totalAmount !== '' && (isNaN(Number(totalAmount)) || Number(totalAmount) < 0)
    ? 'Total amount must be a non-negative number'
    : null;

  const hasErrors = !!supplierIdError || !!invoiceNumberError || !!totalAmountError;

  // In create mode: stage the file. In edit mode: upload immediately.
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    if (!isEdit) {
      setPendingFile(file);
      return;
    }

    setUploading(true);
    setUploadError(null);
    try {
      const updated = await invoiceService.uploadAttachment(venueId, invoice!._id, file);
      setAttachments(updated.attachments ?? []);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } }; message?: string })
        ?.response?.data?.message ?? (err as Error)?.message ?? 'Upload failed';
      setUploadError(msg);
    } finally {
      setUploading(false);
    }
  };

  const handleOpenAttachment = async (attachment: InvoiceAttachment) => {
    if (!invoice) return;
    setOpeningId(attachment._id);
    setOpenError(null);
    try {
      const url = invoiceService.getAttachmentUrl(venueId, invoice._id, attachment._id);
      const response = await api.get(url, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: attachment.mimeType });
      const objectUrl = URL.createObjectURL(blob);
      window.open(objectUrl, '_blank');
      setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } }; message?: string })
        ?.response?.data?.message ?? (err as Error)?.message ?? 'Failed to open file';
      setOpenError(msg);
    } finally {
      setOpeningId(null);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / 1024).toFixed(1)} KB`;
  };

  const mimeLabel = (mimeType: string): string => {
    if (mimeType === 'application/pdf') return 'PDF';
    if (mimeType === 'image/jpeg') return 'JPEG';
    if (mimeType === 'image/png') return 'PNG';
    return mimeType;
  };

  const handleDeleteAttachment = async (attachment: InvoiceAttachment) => {
    if (!invoice) return;
    if (!window.confirm(`Delete "${attachment.originalName}"?`)) return;

    setDeletingId(attachment._id);
    setDeleteError(null);
    try {
      const updated = await invoiceService.deleteAttachment(venueId, invoice._id, attachment.storageKey);
      setAttachments(updated.attachments ?? []);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } }; message?: string })
        ?.response?.data?.message ?? (err as Error)?.message ?? 'Failed to delete attachment';
      setDeleteError(msg);
    } finally {
      setDeletingId(null);
    }
  };

  const handleSubmit = async () => {
    setTouched(true);
    if (!supplierId || invoiceNumber.trim().length === 0) return;
    if (totalAmount !== '' && (isNaN(Number(totalAmount)) || Number(totalAmount) < 0)) return;

    setSaving(true);
    setError(null);
    try {
      const payload = {
        supplierId,
        invoiceNumber: invoiceNumber.trim(),
        invoiceDate:   invoiceDate || undefined,
        dueDate:       dueDate || undefined,
        totalAmount:   totalAmount !== '' ? Number(totalAmount) : undefined,
        notes:         notes.trim() || undefined,
      };

      if (isEdit) {
        await invoiceService.updateInvoice(venueId, invoice!._id, { ...payload, status });
        onSuccess();
        onClose();
        return;
      }

      // Create mode: create first, then upload if a file was staged
      const created = await invoiceService.createInvoice(venueId, { purchaseOrderId, ...payload });

      if (pendingFile) {
        try {
          await invoiceService.uploadAttachment(venueId, created._id, pendingFile);
        } catch (uploadErr: unknown) {
          const msg = (uploadErr as { response?: { data?: { message?: string } }; message?: string })
            ?.response?.data?.message ?? (uploadErr as Error)?.message ?? 'Upload failed';
          // Invoice was created — reload the list so it appears, but stay open to show the error
          onSuccess();
          setError(`Invoice created but file upload failed: ${msg}. You can attach the file by editing the invoice.`);
          setSaving(false);
          return;
        }
      }

      onSuccess();
      onClose();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } }; message?: string })
        ?.response?.data?.message ?? (err as Error)?.message ?? 'Failed to save invoice';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving || uploading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: 700 }}>
        {isEdit ? 'Edit Invoice' : 'Add Invoice'}
      </DialogTitle>

      <DialogContent sx={{ pt: '8px !important' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <FormControl fullWidth error={!!supplierIdError} required sx={{ mb: 2 }}>
          <InputLabel>Supplier</InputLabel>
          <Select
            label="Supplier"
            value={supplierId}
            onChange={e => setSupplierId(e.target.value)}
          >
            {suppliers.length === 0 && (
              <MenuItem disabled value="">Loading suppliers…</MenuItem>
            )}
            {suppliers.map(s => (
              <MenuItem key={s._id} value={s._id}>{s.name}</MenuItem>
            ))}
          </Select>
          {supplierIdError && (
            <Box component="span" sx={{ fontSize: '0.75rem', color: 'error.main', mt: 0.5, ml: 1.75 }}>
              {supplierIdError}
            </Box>
          )}
        </FormControl>

        {isEdit && (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={status}
              onChange={e => setStatus(e.target.value as InvoiceStatus)}
            >
              {INVOICE_STATUSES.map(s => (
                <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <TextField
          label="Invoice Number"
          value={invoiceNumber}
          onChange={e => setInvoiceNumber(e.target.value)}
          error={!!invoiceNumberError}
          helperText={invoiceNumberError ?? ' '}
          fullWidth
          required
          autoFocus
          sx={{ mb: 1 }}
          inputProps={{ maxLength: 200 }}
        />

        <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
          <TextField
            label="Invoice Date"
            type="date"
            value={invoiceDate}
            onChange={e => setInvoiceDate(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Due Date"
            type="date"
            value={dueDate}
            onChange={e => setDueDate(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </Box>

        <TextField
          label="Total Amount"
          value={totalAmount}
          onChange={e => setTotalAmount(e.target.value)}
          error={!!totalAmountError}
          helperText={totalAmountError ?? ' '}
          fullWidth
          sx={{ mb: 1 }}
          inputProps={{ inputMode: 'decimal' }}
        />

        <TextField
          label="Notes"
          value={notes}
          onChange={e => setNotes(e.target.value)}
          fullWidth
          multiline
          minRows={2}
          inputProps={{ maxLength: 2000 }}
        />

        <Divider sx={{ my: 2 }} />

        {/* Attachment section — file picker in create mode, full list in edit mode */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {isEdit ? 'Attachments' : 'Attachment (optional)'}
          </Typography>
          <Button
            size="small"
            startIcon={uploading ? <CircularProgress size={14} color="inherit" /> : <AttachFileIcon />}
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || saving}
            sx={{ textTransform: 'none' }}
          >
            {uploading ? 'Uploading…' : 'Attach File'}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,image/jpeg,image/png"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
        </Box>

        {/* Create mode: show staged file name */}
        {!isEdit && (
          pendingFile ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 0.5 }}>
              <AttachFileIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2" noWrap sx={{ flex: 1, maxWidth: 340 }}>
                {pendingFile.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatFileSize(pendingFile.size)}
              </Typography>
              <IconButton
                size="small"
                onClick={() => { setPendingFile(null); }}
                disabled={saving}
                title="Remove file"
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
              No file selected.
            </Typography>
          )
        )}

        {/* Edit mode: upload error, delete error, full attachment list */}
        {isEdit && (
          <>
            {uploadError && (
              <Alert severity="error" sx={{ mb: 1 }} onClose={() => setUploadError(null)}>
                {uploadError}
              </Alert>
            )}

            {openError && (
              <Alert severity="error" sx={{ mb: 1 }} onClose={() => setOpenError(null)}>
                {openError}
              </Alert>
            )}

            {deleteError && (
              <Alert severity="error" sx={{ mb: 1 }} onClose={() => setDeleteError(null)}>
                {deleteError}
              </Alert>
            )}

            {attachments.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
                No attachments yet.
              </Typography>
            ) : (
              <List dense disablePadding>
                {attachments.map(a => {
                  const busy = openingId === a._id || deletingId === a._id;
                  return (
                    <ListItem
                      key={a._id}
                      disableGutters
                      secondaryAction={
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenAttachment(a)}
                            disabled={busy}
                            title="Open file"
                          >
                            {openingId === a._id
                              ? <CircularProgress size={16} />
                              : <OpenInNewIcon fontSize="small" />}
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteAttachment(a)}
                            disabled={busy}
                            title="Delete attachment"
                            color="error"
                          >
                            {deletingId === a._id
                              ? <CircularProgress size={16} color="error" />
                              : <DeleteOutlineIcon fontSize="small" />}
                          </IconButton>
                        </Box>
                      }
                    >
                      <ListItemText
                        primary={a.originalName}
                        secondary={`${formatFileSize(a.sizeBytes)} · ${mimeLabel(a.mimeType)}`}
                        primaryTypographyProps={{ variant: 'body2', noWrap: true, sx: { maxWidth: 300 } }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                  );
                })}
              </List>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button
          onClick={onClose}
          disabled={saving || uploading}
          sx={{ textTransform: 'none' }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving || uploading || (touched && hasErrors)}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Invoice'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvoiceDialog;
